// components/products/VariantsForm.tsx
import { VariantFormData } from '@cabukaka/types';
import { X, Plus, Image as ImageIcon, Trash2 } from 'lucide-react';
import { useState, useEffect } from 'react';

interface ExtendedVariantFormData extends VariantFormData {
  existingImages?: string[];
}

interface VariantsFormProps {
  variants: ExtendedVariantFormData[];
  onChange: (variants: ExtendedVariantFormData[]) => void;
}

export default function VariantsForm({ variants, onChange }: VariantsFormProps) {
  const [imagePreviews, setImagePreviews] = useState<{ [key: number]: string[] }>({});

  useEffect(() => {
    const previews: { [key: number]: string[] } = {};
    variants.forEach((variant, idx) => {
      if (variant.localImages && variant.localImages.length > 0) {
        previews[idx] = variant.localImages.map(file => URL.createObjectURL(file));
      } else {
        previews[idx] = [];
      }
    });
    setImagePreviews(previews);

    return () => {
      Object.values(previews).forEach(urls => {
        if (urls && urls.length > 0) {
          urls.forEach(url => URL.revokeObjectURL(url));
        }
      });
    };
  }, [variants]);

  const addVariant = () => {
    onChange([
      ...variants,
      {
        name: '',
        value: '',
        originalPrice: '',
        price: 0,
        description: '',
        localImages: [],
        existingImages: [],
        stock: 0
      }
    ]);
  };

  const updateVariant = (index: number, field: keyof ExtendedVariantFormData, value: any) => {
    const newVariants = [...variants];
    if (newVariants[index]) {
      newVariants[index] = { ...newVariants[index], [field]: value };
      onChange(newVariants);
    }
  };

  const removeVariant = (index: number) => {
    const newVariants = variants.filter((_, i) => i !== index);
    onChange(newVariants);
  };

  const handleImageUpload = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const currentVariant = variants[index];
    if (!currentVariant) return;

    const currentImages = currentVariant.localImages || [];
    const existingCount = currentVariant.existingImages?.length || 0;

    if (currentImages.length + files.length + existingCount > 5) {
      alert('Maximum 5 images par variante');
      return;
    }

    const newVariants = [...variants];
    if (newVariants[index]) {
      newVariants[index].localImages = [...currentImages, ...files];
      onChange(newVariants);
    }
  };

  const removeNewImage = (variantIndex: number, imageIndex: number) => {
    const newVariants = [...variants];
    const variant = newVariants[variantIndex];
    if (variant && variant.localImages) {
      variant.localImages = variant.localImages.filter((_, i) => i !== imageIndex);
      onChange(newVariants);
    }
  };

  const removeExistingImage = (variantIndex: number, imageIndex: number) => {
    const newVariants = [...variants];
    const variant = newVariants[variantIndex];
    if (variant && variant.existingImages) {
      variant.existingImages = variant.existingImages.filter((_, i) => i !== imageIndex);
      onChange(newVariants);
    }
  };

  if (variants.length === 0) {
    return (
      <div className="text-center py-8 bg-gray-50 rounded-lg border border-dashed border-gray-300">
        <p className="text-gray-500 text-sm">Aucune variante</p>
        <button type="button" onClick={addVariant} className="text-orange-600 text-sm mt-1 hover:underline">
          Ajouter une variante
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <label className="block text-sm font-medium text-gray-700">Variantes</label>
        <button
          type="button"
          onClick={addVariant}
          className="flex items-center gap-1 text-orange-600 hover:text-orange-700 text-sm font-medium"
        >
          <Plus className="w-4 h-4" />
          Ajouter une variante
        </button>
      </div>

      {variants.map((variant, idx) => (
        <div key={idx} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
          <div className="flex justify-between items-start mb-3">
            <span className="font-medium text-gray-700">Variante {idx + 1}</span>
            <button type="button" onClick={() => removeVariant(idx)} className="text-red-500 hover:text-red-600">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 mb-3">
            <input
              type="text"
              placeholder="Nom (ex: Taille)"
              value={variant.name}
              onChange={(e) => updateVariant(idx, 'name', e.target.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
            />
            <input
              type="text"
              placeholder="Valeur (ex: XL)"
              value={variant.value}
              onChange={(e) => updateVariant(idx, 'value', e.target.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
            />
            <input
              type="text"
              placeholder="Prix original (optionnel)"
              value={variant.originalPrice}
              onChange={(e) => updateVariant(idx, 'originalPrice', e.target.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
            />
            <input
              type="number"
              placeholder="Prix"
              value={variant.price || ''}
              onChange={(e) => updateVariant(idx, 'price', parseFloat(e.target.value))}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
            />
            <input
              type="number"
              placeholder="Stock"
              value={variant.stock || ''}
              onChange={(e) => updateVariant(idx, 'stock', parseInt(e.target.value))}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
            />
            <input
              type="text"
              placeholder="Description (optionnel)"
              value={variant.description || ''}
              onChange={(e) => updateVariant(idx, 'description', e.target.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
            />
          </div>

          {/* Images de la variante */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-2">Images de la variante (optionnel)</label>
            <div className="flex flex-wrap gap-2">
              {/* Images existantes */}
              {variant.existingImages && variant.existingImages.map((url, imgIdx) => (
                <div key={`existing-${idx}-${imgIdx}`} className="relative w-16 h-16 rounded-lg overflow-hidden border border-gray-200 bg-gray-50">
                  <img src={url} alt={`Variant ${idx + 1} image ${imgIdx + 1}`} className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removeExistingImage(idx, imgIdx)}
                    className="absolute top-0 right-0 p-0.5 bg-red-500 text-white rounded-full hover:bg-red-600"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
              {/* Nouvelles images en preview */}
              {imagePreviews[idx] && imagePreviews[idx].map((url, imgIdx) => (
                <div key={`new-${idx}-${imgIdx}`} className="relative w-16 h-16 rounded-lg overflow-hidden border border-gray-200 bg-gray-50">
                  <img src={url} alt={`Preview ${idx + 1}`} className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removeNewImage(idx, imgIdx)}
                    className="absolute top-0 right-0 p-0.5 bg-red-500 text-white rounded-full hover:bg-red-600"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
              {((variant.existingImages?.length || 0) + (variant.localImages?.length || 0)) < 5 && (
                <label className="w-16 h-16 rounded-lg border-2 border-dashed border-gray-300 hover:border-orange-500 transition-colors cursor-pointer flex flex-col items-center justify-center bg-gray-50 hover:bg-orange-50">
                  <ImageIcon className="w-5 h-5 text-gray-400" />
                  <span className="text-[10px] text-gray-500">Ajouter</span>
                  <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => handleImageUpload(idx, e)} />
                </label>
              )}
            </div>
            <p className="text-xs text-gray-400 mt-1">Maximum 5 images. Laissez vide pour utiliser l'image principale du produit.</p>
          </div>
        </div>
      ))}
    </div>
  );
}